import { useEffect, useState, useRef, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { UsersThree, UploadSimple, Trash, CheckCircle, Star, DownloadSimple } from "@phosphor-icons/react";

const ROLE_FIELDS = [
  ["standard", "Standard"],
  ["req", "Requirement #"],
  ["text", "Text of Requirement"],
  ["details", "Responsibility Details"],
  ["effective_date", "Effective Date"],
  ["shared", "Shared / Both flag"],
  ["split", "Split flag"],
  ["na", "Not-Applicable flag"],
];

const TYPE_BADGE = {
  sole: "border-sky-300 bg-sky-50 text-sky-700",
  shared_both: "border-violet-300 bg-violet-50 text-violet-700",
  split: "border-amber-300 bg-amber-50 text-amber-700",
  not_applicable: "border-gray-300 bg-gray-50 text-gray-500",
  unspecified: "border-gray-300 bg-gray-50 text-gray-500",
};

export default function CfrMatrix() {
  const { canEdit } = useAuth();
  const [matrices, setMatrices] = useState(null);
  const [profileFuncs, setProfileFuncs] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [staged, setStaged] = useState(null);
  const [form, setForm] = useState({ title: "", registered_function: "", area: "", effective_date: "" });
  const [map, setMap] = useState({});
  const [parties, setParties] = useState([]);
  const [myEntityIndex, setMyEntityIndex] = useState(null);
  const [committing, setCommitting] = useState(false);
  const fileRef = useRef(null);
  const pubRef = useRef(null);
  const [pubData, setPubData] = useState(null);   // { entities:[], rows:[] }
  const [pubEntity, setPubEntity] = useState("");
  const [pubFilter, setPubFilter] = useState("");
  const [pubParsing, setPubParsing] = useState(false);

  const onPublicFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPubParsing(true);
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/cfr/parse-public", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPubData({ entities: data.entities || [], rows: data.rows || [], source: data.filename || "" });
      setPubEntity("");
      setPubFilter("");
      toast.success(`Parsed public CFR — ${data.count} requirements across ${(data.entities || []).length} entities (read-only).`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not parse public CFR."); }
    finally { setPubParsing(false); if (pubRef.current) pubRef.current.value = ""; }
  };

  const [pubExporting, setPubExporting] = useState("");
  const exportPublic = async (fmt) => {
    if (!pubEntity) return toast.error("Choose an entity to export.");
    setPubExporting(fmt);
    try {
      const rows = pubRowsFiltered.map((r) => ({
        standard: r.standard, req: r.req, effective_date: r.effective_date,
        responsibility: r.responsibilities?.[pubEntity] || "",
      }));
      const resp = await api.post(`/cfr/export-public?fmt=${fmt}`,
        { entity: pubEntity, source_name: pubData?.source || "", rows },
        { responseType: "blob" });
      const cd = resp.headers["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/.exec(cd);
      const name = m ? m[1] : `Public_CFR.${fmt}`;
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a"); a.href = url; a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`Exported ${rows.length} requirements (${fmt.toUpperCase()}).`);
    } catch (err) {
      let detail = "Export failed.";
      try { detail = formatApiError(JSON.parse(await err.response.data.text()).detail); } catch (e) { /* keep */ }
      toast.error(detail);
    } finally { setPubExporting(""); }
  };

  const RESP_BADGE = {
    "Full": "border-emerald-300 bg-emerald-50 text-emerald-700",
    "Normal": "border-sky-300 bg-sky-50 text-sky-700",
    "Partial": "border-amber-300 bg-amber-50 text-amber-700",
    "None": "border-gray-300 bg-gray-50 text-gray-500",
    "N/A": "border-gray-300 bg-gray-50 text-gray-400",
  };
  const pubRowsFiltered = (pubData?.rows || []).filter((r) =>
    !pubFilter.trim() || (r.standard || "").toLowerCase().includes(pubFilter.trim().toLowerCase()));

  const load = useCallback(() => {
    api.get("/cfr/matrices").then((r) => setMatrices(r.data)).catch(() => setMatrices([]));
    api.get("/entity-profile").then((r) => setProfileFuncs(r.data?.registered_functions || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/cfr/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setStaged(data);
      setMap({ ...data.map });
      setParties((data.parties || []).map((p) => ({ ...p, name: p.header })));
      setMyEntityIndex(null);
      setForm({ title: `${file.name.replace(/\.[^.]+$/, "")}`.slice(0, 80) || "CFR Responsibility Matrix",
        registered_function: profileFuncs[0] || "", area: "", effective_date: "" });
      toast.success(`Parsed ${data.preview?.length || 0}+ rows — verify column mapping and commit`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not parse the file."); }
    finally { setParsing(false); }
  };

  const headerOpts = (staged?.header || []).map((h, i) => ({ value: i, label: `${i}: ${h || "(blank)"}` }));
  const setRole = (role, val) => setMap((m) => ({ ...m, [role]: val === "" ? null : Number(val) }));
  const renameParty = (idx, name) => setParties((ps) => ps.map((p) => (p.index === idx ? { ...p, name } : p)));
  const removeParty = (idx) => { setParties((ps) => ps.filter((p) => p.index !== idx)); if (myEntityIndex === idx) setMyEntityIndex(null); };

  const commit = async () => {
    if (!form.title.trim()) return toast.error("Give the matrix a title");
    if (!form.registered_function.trim()) return toast.error("Set the registered function this matrix coordinates");
    if (parties.length === 0) return toast.error("Name at least one responsibility party");
    if (parties.some((p) => !p.name.trim())) return toast.error("Every kept party needs a name");
    const party_names = Object.fromEntries(parties.map((p) => [String(p.index), p.name.trim()]));
    const my_entity_party = myEntityIndex != null ? party_names[String(myEntityIndex)] : "";
    setCommitting(true);
    try {
      const { data } = await api.post("/cfr/commit", {
        token: staged.token, ...form, my_entity_party, party_names,
        column_map: { ...map, parties: parties.map((p) => p.index) },
      });
      toast.success(`Matrix committed (v${data.version}) — ${data.matched}/${data.rows} rows linked to standards`);
      setStaged(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Commit failed."); }
    finally { setCommitting(false); }
  };

  const activate = async (m) => {
    try { await api.post(`/cfr/matrices/${m.id}/activate`); toast.success("Activated"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (m) => {
    if (!window.confirm(`Delete "${m.title}" v${m.version}? This cannot be undone.`)) return;
    try { await api.delete(`/cfr/matrices/${m.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-8 max-w-6xl" data-testid="cfr-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display font-bold text-2xl text-[#071B3E] dark:text-white flex items-center gap-2">
            <UsersThree size={26} weight="fill" className="text-indigo-700" />Coordinated Functional Registration (CFR)
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
            When two or more entities are registered for the <strong>same NERC function</strong> in the same coordination
            area, upload the approved responsibility matrix. ClaroShield captures who performs each task (sole, both,
            split, or not applicable) and links it to your Standards Library so it flows into RSAWs, policies and procedures.
          </p>
        </div>
        {canEdit && (
          <div>
            <input ref={fileRef} type="file" accept=".xlsx,.csv" className="hidden" onChange={onFile} data-testid="cfr-file-input" />
            <Button data-testid="cfr-upload-btn" onClick={() => fileRef.current?.click()} disabled={parsing}
              className="rounded-none bg-[#071B3E] hover:bg-[#0c2a5e]">
              <UploadSimple size={16} className="mr-1.5" />{parsing ? "Parsing…" : "Upload matrix"}
            </Button>
          </div>
        )}
      </div>

      <div className="mt-4 text-[12px] text-amber-900 bg-amber-50 border border-amber-300 px-3 py-2" data-testid="cfr-tier-note">
        <b>Public vs. entity CFR:</b> the generalized public NERC CFR is viewable on every plan. Entity-specific
        responsibility matrices (e.g. a regional TRE CFR listing your entities) are <b>not public</b> — uploading and
        saving them is a <b>ClaroShield</b> feature.
      </div>

      <div className="mt-4 border border-border" data-testid="cfr-public-section">
        <div className="border-b border-border bg-secondary/40 px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap">
          <span className="font-display font-bold text-sm">Public NERC CFR (read-only)</span>
          <div>
            <input ref={pubRef} type="file" accept=".xlsx,.csv" className="hidden" onChange={onPublicFile} data-testid="cfr-public-input" />
            <Button size="sm" variant="outline" className="rounded-none" disabled={pubParsing}
              onClick={() => pubRef.current?.click()} data-testid="cfr-public-upload-btn">
              <UploadSimple size={15} className="mr-1.5" />{pubParsing ? "Parsing…" : "View public CFR"}
            </Button>
          </div>
        </div>
        {pubData === null ? (
          <div className="px-4 py-6 text-sm text-muted-foreground">
            Upload the generalized public NERC CFR (e.g. <code>tre-cfr000616</code>) to view its standards & requirements, then pick an entity to see its responsibility. Available on every plan — nothing is saved.
          </div>
        ) : pubData.rows.length === 0 ? (
          <div className="px-4 py-6 text-sm text-muted-foreground">No requirements found in that file.</div>
        ) : (
          <>
            <div className="px-4 py-3 border-b border-border flex items-end gap-3 flex-wrap bg-background">
              <div className="min-w-[280px] flex-1">
                <Label className="text-xs text-muted-foreground">Entity (Responsibilities column)</Label>
                <select data-testid="cfr-public-entity" value={pubEntity} onChange={(e) => setPubEntity(e.target.value)}
                  className="w-full border border-border bg-background px-2 py-1.5 text-sm rounded-none mt-1">
                  <option value="">— Choose an entity to see its responsibility —</option>
                  {pubData.entities.map((en) => <option key={en} value={en}>{en}</option>)}
                </select>
              </div>
              <div className="w-52">
                <Label className="text-xs text-muted-foreground">Filter by Standard</Label>
                <Input data-testid="cfr-public-filter" value={pubFilter} onChange={(e) => setPubFilter(e.target.value)}
                  placeholder="e.g. CIP-002" className="rounded-none mt-1 h-9" />
              </div>
              <div className="text-xs text-muted-foreground pb-2">{pubRowsFiltered.length} requirements</div>
              <div className="flex items-center gap-2 pb-1 ml-auto">
                <Button size="sm" variant="outline" className="rounded-none" data-testid="cfr-public-export-xlsx"
                  disabled={!pubEntity || !!pubExporting} onClick={() => exportPublic("xlsx")}>
                  <DownloadSimple size={15} className="mr-1.5" />{pubExporting === "xlsx" ? "Exporting…" : "Excel"}
                </Button>
                <Button size="sm" variant="outline" className="rounded-none" data-testid="cfr-public-export-pdf"
                  disabled={!pubEntity || !!pubExporting} onClick={() => exportPublic("pdf")}>
                  <DownloadSimple size={15} className="mr-1.5" />{pubExporting === "pdf" ? "Exporting…" : "PDF"}
                </Button>
              </div>
            </div>
            <div className="overflow-x-auto max-h-[520px]">
              <table className="w-full text-sm" data-testid="cfr-public-table">
                <thead className="bg-[#071B3E] text-white sticky top-0"><tr className="text-left">
                  <th className="p-2 whitespace-nowrap">Standard</th><th className="p-2 whitespace-nowrap">Requirement</th>
                  <th className="p-2 whitespace-nowrap">Effective Date</th><th className="p-2">Responsibility</th>
                </tr></thead>
                <tbody>
                  {pubRowsFiltered.map((r, i) => {
                    const val = pubEntity ? (r.responsibilities?.[pubEntity] || "") : "";
                    return (
                      <tr key={i} className="border-t border-border align-top" data-testid={`cfr-public-row-${i}`}>
                        <td className="p-2 font-medium whitespace-nowrap">{r.standard}</td>
                        <td className="p-2 whitespace-nowrap">{r.req}</td>
                        <td className="p-2 text-xs whitespace-nowrap">{r.effective_date || "—"}</td>
                        <td className="p-2">
                          {!pubEntity ? (
                            <span className="text-xs text-muted-foreground italic">select an entity above</span>
                          ) : val ? (
                            <span className={`text-[11px] uppercase font-bold border px-1.5 py-0.5 ${RESP_BADGE[val] || "border-gray-300 bg-gray-50 text-gray-500"}`}>{val}</span>
                          ) : <span className="text-xs text-muted-foreground">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <div className="mt-6 border border-border" data-testid="cfr-list">
        <div className="border-b border-border bg-secondary/40 px-4 py-2.5 font-display font-bold text-sm">Uploaded matrices</div>
        {matrices === null ? (
          <div className="px-4 py-6 text-sm text-muted-foreground">Loading…</div>
        ) : matrices.length === 0 ? (
          <div className="px-4 py-6 text-sm text-muted-foreground" data-testid="cfr-empty">
            No CFR matrices yet. Upload an approved responsibility matrix (.xlsx or .csv) to get started.
          </div>
        ) : (
          <div className="divide-y divide-border">
            {matrices.map((m) => (
              <div key={m.id} data-testid={`cfr-row-${m.id}`} className="px-4 py-3 flex items-center gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm">{m.title}</span>
                    <span className="text-[10px] font-mono border border-border px-1.5 py-0.5">v{m.version}</span>
                    {m.active
                      ? <span data-testid={`cfr-active-${m.id}`} className="text-[10px] uppercase font-bold border border-emerald-400 bg-emerald-50 text-emerald-700 px-1.5 py-0.5">Active</span>
                      : <span className="text-[10px] uppercase font-bold border border-gray-300 bg-gray-50 text-gray-500 px-1.5 py-0.5">Inactive</span>}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {m.registered_function || "—"}{m.area ? ` · ${m.area}` : ""} · {m.matched_count || 0} linked ·
                    parties: {(m.parties || []).join(", ") || "—"}{m.my_entity_party ? ` · you = ${m.my_entity_party}` : ""}
                    {m.effective_date ? ` · eff ${m.effective_date}` : ""}
                  </div>
                </div>
                {canEdit && (
                  <div className="flex items-center gap-2 shrink-0">
                    {!m.active && (
                      <button data-testid={`cfr-activate-${m.id}`} onClick={() => activate(m)}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 border border-emerald-300 px-2 py-1 hover:bg-emerald-50">
                        <CheckCircle size={13} />Set active
                      </button>
                    )}
                    <button data-testid={`cfr-delete-${m.id}`} onClick={() => remove(m)} className="text-muted-foreground hover:text-red-600"><Trash size={16} /></button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!staged} onOpenChange={(o) => { if (!o) setStaged(null); }}>
        <DialogContent className="rounded-none max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="cfr-review-dialog">
          <DialogHeader><DialogTitle>Verify &amp; commit — {staged?.filename}</DialogTitle>
            <DialogDescription>Confirm the column mapping and name each responsibility party, then commit the approved matrix.</DialogDescription>
          </DialogHeader>
          {staged && (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Title</Label>
                  <Input data-testid="cfr-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-none mt-1" /></div>
                <div><Label>Registered Function this matrix coordinates</Label>
                  <Input data-testid="cfr-function" list="cfr-func-list" value={form.registered_function}
                    onChange={(e) => setForm({ ...form, registered_function: e.target.value })} placeholder="e.g. Transmission Operator" className="rounded-none mt-1" />
                  <datalist id="cfr-func-list">{profileFuncs.map((f) => <option key={f} value={f} />)}</datalist></div>
                <div><Label>Coordination Area (optional)</Label>
                  <Input data-testid="cfr-area" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} placeholder="e.g. XYZ TOP Area" className="rounded-none mt-1" /></div>
                <div><Label>Effective Date (optional)</Label>
                  <Input data-testid="cfr-effdate" type="date" value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} className="rounded-none mt-1" /></div>
              </div>

              <div className="border border-border p-3">
                <div className="font-display font-bold text-sm mb-2">Column mapping</div>
                <div className="grid sm:grid-cols-2 gap-2">
                  {ROLE_FIELDS.map(([role, label]) => (
                    <div key={role} className="flex items-center gap-2">
                      <span className="text-xs w-40 shrink-0 text-muted-foreground">{label}</span>
                      <select data-testid={`cfr-map-${role}`} value={map[role] ?? ""} onChange={(e) => setRole(role, e.target.value)}
                        className="flex-1 border border-border bg-background px-2 py-1 text-xs rounded-none">
                        <option value="">— none —</option>
                        {headerOpts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border border-border p-3">
                <div className="font-display font-bold text-sm mb-1">Responsibility parties</div>
                <p className="text-xs text-muted-foreground mb-2">Name each party column and mark which one is <strong>your entity</strong> (used for applicability in policies &amp; RSAWs).</p>
                <div className="space-y-2">
                  {parties.map((p) => (
                    <div key={p.index} className="flex items-center gap-2" data-testid={`cfr-party-${p.index}`}>
                      <button type="button" data-testid={`cfr-myentity-${p.index}`} onClick={() => setMyEntityIndex(p.index)}
                        title="Mark as my entity"
                        className={`inline-flex items-center gap-1 text-[11px] font-semibold border px-2 py-1.5 rounded-none ${myEntityIndex === p.index ? "border-indigo-600 bg-indigo-600 text-white" : "border-indigo-300 text-indigo-700 hover:bg-indigo-50"}`}>
                        <Star size={12} weight={myEntityIndex === p.index ? "fill" : "regular"} />{myEntityIndex === p.index ? "My entity" : "Set as mine"}
                      </button>
                      <Input value={p.name} onChange={(e) => renameParty(p.index, e.target.value)}
                        data-testid={`cfr-party-name-${p.index}`} className="rounded-none flex-1" />
                      <span className="text-[11px] text-muted-foreground w-24 shrink-0">col {p.index} · {p.marks || 0} marks</span>
                      <button type="button" onClick={() => removeParty(p.index)} className="text-muted-foreground hover:text-red-600"><Trash size={15} /></button>
                    </div>
                  ))}
                  {parties.length === 0 && <p className="text-xs text-red-600">No party columns detected — check the column mapping above.</p>}
                </div>
              </div>

              <div className="border border-border">
                <div className="border-b border-border bg-secondary/40 px-3 py-2 font-display font-bold text-sm">Preview (first rows)</div>
                <div className="max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-secondary/60"><tr className="text-left">
                      <th className="px-2 py-1">Standard</th><th className="px-2 py-1">Req</th>
                      <th className="px-2 py-1">Responsibility</th><th className="px-2 py-1">Marked</th></tr></thead>
                    <tbody>
                      {(staged.preview || []).map((row, i) => (
                        <tr key={i} className="border-t border-border/60" data-testid={`cfr-preview-row-${i}`}>
                          <td className="px-2 py-1 font-mono">{row.standard}</td>
                          <td className="px-2 py-1 font-mono">{row.req}</td>
                          <td className="px-2 py-1"><span className={`text-[10px] uppercase font-bold border px-1 py-0.5 ${TYPE_BADGE[row.responsibility_type] || TYPE_BADGE.unspecified}`}>{row.responsibility_type}</span></td>
                          <td className="px-2 py-1">{(row.assignments || []).join(", ") || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <Button data-testid="cfr-commit-btn" onClick={commit} disabled={committing}
                className="rounded-none w-full bg-primary hover:bg-primary/90">
                {committing ? "Committing…" : "Commit matrix"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
